/**
 * Combobox Component Module
 *
 * Unified, accessible combobox with advanced features:
 * - Single & multi-select
 * - Search with fuzzy matching
 * - Virtual scrolling (1000+ items)
 * - Keyboard navigation
 * - Async data loading
 * - React Hook Form integration
 * - Full accessibility (WCAG 2.1 AA)
 *
 * @example
 * ```tsx
 * import { Combobox, ComboboxField } from '../../index';
 *
 * // Simple usage
 * <Combobox
 *   options={countries}
 *   value={country}
 *   onChange={setCountry}
 *   search
 *   placeholder="Select country..."
 * />
 *
 * // With React Hook Form
 * <ComboboxField
 *   control={control}
 *   name="country"
 *   label="Country"
 *   options={countries}
 *   rules={{ required: 'Required' }}
 * />
 * ```
 */

// ============================================================================
// Main Components
// ============================================================================

export { Combobox } from './combobox';
export { ComboboxField } from './combobox-field';

// ============================================================================
// Sub-components (for advanced composition)
// ============================================================================

export { ComboboxTrigger } from './combobox-trigger';
export { ComboboxContent } from './combobox-content';
export { ComboboxSearch } from './combobox-search';
export { ComboboxItem } from './combobox-item';
export { ComboboxVirtualList } from './combobox-virtual-list';
export { ComboboxMultiValue } from './combobox-multi-value';
export { ComboboxEmpty } from './combobox-empty';
export { ComboboxLoading } from './combobox-loading';

// ============================================================================
// Hooks
// ============================================================================

export { useComboboxSearch } from './hooks/use-combobox-search';
export type { UseComboboxSearchProps, UseComboboxSearchReturn } from './hooks/use-combobox-search';

export { useComboboxKeyboard } from './hooks/use-combobox-keyboard';
export type {
  UseComboboxKeyboardProps,
  UseComboboxKeyboardReturn,
} from './hooks/use-combobox-keyboard';

// ============================================================================
// Types
// ============================================================================

export type {
  // Core types
  ComboboxOption,
  ComboboxOptionGroup,
  ComboboxProps,

  // Configuration types
  ComboboxSearchConfig,
  ComboboxVirtualConfig,
  ComboboxAsyncConfig,
  ComboboxMultiConfig,

  // Component props
  ComboboxTriggerProps,
  ComboboxContentProps,
  ComboboxSearchProps,
  ComboboxItemProps,
  ComboboxVirtualListProps,
  ComboboxMultiValueProps,
  ComboboxEmptyProps,
  ComboboxLoadingProps,

  // Field props
  ComboboxFieldProps,
  ComboboxControllerProps,

  // State types
  ComboboxItemState,
} from './combobox.types';

// ============================================================================
// Utilities
// ============================================================================

export { fuzzyMatch, flattenOptions, filterOptions } from './combobox.utils';
